/**
 * Central route map — single source of truth for bilingual routing.
 *
 * Each entry maps a canonical route KEY to its EN and FR URL paths, plus:
 *   - `key`      : internal key used by <LocLink to="key">
 *   - `en`       : EN URL path (mounted under /en/)
 *   - `fr`       : FR URL path (mounted under /fr/)
 *   - `legacy`   : old, pre-localized URL path (used for 301 redirects)
 *   - `indexable`: whether the route should appear in sitemap.xml
 */
export const routes = {
  home: {
    key: 'home',
    en: '/en',
    fr: '/fr',
    legacy: '/',
    indexable: true,
    changefreq: 'weekly',
    priority: 1.0,
  },
  products: {
    key: 'products',
    en: '/en/products',
    fr: '/fr/produits',
    legacy: '/products',
    indexable: true,
    changefreq: 'weekly',
    priority: 0.9,
  },
  story: {
    key: 'story',
    en: '/en/story',
    fr: '/fr/notre-histoire',
    legacy: '/story',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  healthcare: {
    key: 'healthcare',
    en: '/en/healthcare',
    fr: '/fr/sante',
    legacy: '/healthcare',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.8,
  },
  contact: {
    key: 'contact',
    en: '/en/contact',
    fr: '/fr/contact',
    legacy: '/contact',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.6,
  },
  faq: {
    key: 'faq',
    en: '/en/faq',
    fr: '/fr/faq',
    legacy: '/faq',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  privacy: {
    key: 'privacy',
    en: '/en/privacy',
    fr: '/fr/confidentialite',
    legacy: '/privacy',
    indexable: true,
    changefreq: 'yearly',
    priority: 0.3,
  },
  terms: {
    key: 'terms',
    en: '/en/terms',
    fr: '/fr/conditions',
    legacy: '/terms',
    indexable: true,
    changefreq: 'yearly',
    priority: 0.3,
  },
  guides: {
    key: 'guides',
    en: '/en/guides',
    fr: '/fr/guides',
    legacy: '/guides',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.5,
  },
  projectAct: {
    key: 'projectAct',
    en: '/en/project-act',
    fr: '/fr/projet-act',
    legacy: '/project-act',
    indexable: true,
    changefreq: 'weekly',
    priority: 0.95,
  },
  valto: {
    key: 'valto',
    en: '/en/valtoco',
    fr: '/fr/valtoco',
    legacy: '/valto',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  nayzi: {
    key: 'nayzi',
    en: '/en/nayzilam',
    fr: '/fr/nayzilam',
    legacy: '/nayzi',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  valtocoCase: {
    key: 'valtocoCase',
    en: '/en/valtoco-case',
    fr: '/fr/etui-valtoco',
    legacy: '/valtoco-case',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  hydrocortisoneCase: {
    key: 'hydrocortisoneCase',
    en: '/en/hydrocortisone-case',
    fr: '/fr/etui-hydrocortisone',
    legacy: '/solu-cortef-case',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  epipenCase: {
    key: 'epipenCase',
    en: '/en/epipen-case',
    fr: '/fr/etui-epipen',
    legacy: '/epipen-case',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
  naloxoneCase: {
    key: 'naloxoneCase',
    en: '/en/naloxone-case',
    fr: '/fr/etui-naloxone',
    legacy: '/naloxone-case',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.7,
  },
};

/**
 * The root path `/` is a special x-default page (RootLanguageChooser),
 * separate from the localized homepages under /en/ and /fr/.
 */
export const ROOT_PATH = '/';

/**
 * Canonical origin, used to build absolute URLs for canonicals + hreflang
 * + Open Graph + JSON-LD.
 */
export const CANONICAL_ORIGIN = 'https://myguardpod.com';

/**
 * Language codes actually emitted in <html lang> and hreflang tags.
 * - Français canadien for FR routes (regional target)
 * - English international for EN routes (client instruction)
 */
export const HTML_LANG = { en: 'en', fr: 'fr-CA' };
export const HREFLANG = { en: 'en', fr: 'fr-CA' };

/**
 * Return the URL path of a given route in a given language.
 *   getPath('products', 'fr')      -> '/fr/produits'
 *   getPath('products', 'en')      -> '/en/products'
 */
export const getPath = (routeKey, language = 'en') => {
  const entry = routes[routeKey];
  if (!entry) return language === 'fr' ? '/fr' : '/en';
  return entry[language] || entry.en;
};

/**
 * Return absolute URL for a route + language.
 */
export const getAbsoluteUrl = (routeKey, language = 'en') =>
  `${CANONICAL_ORIGIN}${getPath(routeKey, language)}`;

/**
 * Given a URL pathname, detect which language it belongs to.
 * Returns 'en', 'fr', or null (for the x-default root path or unknown).
 */
export const detectLanguageFromPath = (pathname) => {
  if (!pathname) return null;
  if (pathname === '/' || pathname === '') return null;
  if (pathname.startsWith('/fr/') || pathname === '/fr') return 'fr';
  if (pathname.startsWith('/en/') || pathname === '/en') return 'en';
  return null;
};

/**
 * Given a current pathname, find the route entry that matches.
 * Returns the routeKey or null.
 */
export const findRouteKeyForPath = (pathname) => {
  if (!pathname) return null;
  const normalized = pathname.replace(/\/$/, '') || '/';
  for (const [key, entry] of Object.entries(routes)) {
    if (entry.en === normalized || entry.fr === normalized) return key;
  }
  return null;
};

/**
 * Given the current pathname, return the equivalent path in the other language.
 * If no match is found, falls back to `/en` or `/fr` root.
 */
export const getAlternatePath = (pathname, targetLanguage) => {
  const key = findRouteKeyForPath(pathname);
  if (!key) return targetLanguage === 'fr' ? '/fr' : '/en';
  return routes[key][targetLanguage];
};

export default routes;
